import makeStyles from '@mui/styles/makeStyles';
import React from 'react';

import { RATE_UNAVAILABLE_LABEL } from '@/utils';

import type { PriceSummaryProps } from './types';

const useStyles = makeStyles({
  container: {
    backgroundColor: 'var(--surfaceSecondary)',
    border: '1px solid var(--surfaceBorder)',
    borderRadius: 8,
    padding: 12,
    marginTop: 10,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  label: {
    fontSize: 14,
    color: 'var(--onSurfaceMuted)',
  },
  value: {
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--onSurface)',
  },
  divider: {
    borderTop: '1px solid var(--surfaceBorder)',
    paddingTop: 8,
    marginTop: 8,
  },
  totalValue: {
    fontWeight: 600,
    color: 'var(--onSurface)',
  },
});

/**
 * Calculate dollar price from hourly rate (in cents) and duration
 * Returns price in dollars (rounded to nearest dollar)
 */
function calculateDollarPrice(hourlyRateCents: number, durationMinutes: number): string {
  const durationHours = durationMinutes / 60;
  const totalCents = Math.round(hourlyRateCents * durationHours);
  const dollars = Math.round(totalCents / 100);
  return dollars.toString();
}

/**
 * Dollar price for display.
 *
 * A null rate means the API could not price the expert. Mirrors calculateCreditPrice:
 * say so rather than rendering "$0" or a figure derived from a substituted rate — the
 * customer consents to this number, so a plausible-looking wrong price is worse than
 * an obvious absence (ENG-5571).
 */
function dollarPriceDisplay(hourlyRateCents: number | null, durationMinutes: number): string {
  if (hourlyRateCents === null) return RATE_UNAVAILABLE_LABEL;
  return `$${calculateDollarPrice(hourlyRateCents, durationMinutes)}`;
}

/**
 * Calculate credit price from credit rate and duration
 * Credits formula: creditRate * (duration/60)
 *
 * A null rate means the API could not price the expert, so say the rate is unavailable
 * instead of multiplying out to "0.00 credits", which reads as a free call (ENG-5283).
 */
function calculateCreditPrice(creditRate: number | null, durationMinutes: number): string {
  if (creditRate === null) return RATE_UNAVAILABLE_LABEL;

  const credits = creditRate * (durationMinutes / 60);
  return `${credits.toFixed(2)} credits`;
}

/**
 * Format a USD-cents amount as a dollar string: whole dollars when exact,
 * otherwise two decimals. The SMB booking fee is charged verbatim in cents, so
 * the fee line and inclusive total must not be rounded to whole dollars or the
 * displayed amount would drift from what Stripe charges.
 */
function formatDollars(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? dollars.toString() : dollars.toFixed(2);
}

export function PriceSummary({
  duration,
  pricing,
  showChargeNote = true,
  bookingFeeCents,
}: PriceSummaryProps): JSX.Element {
  const s = useStyles();

  const priceDisplay =
    pricing.type === 'dollars'
      ? dollarPriceDisplay(pricing.hourlyRate, duration)
      : calculateCreditPrice(pricing.creditRate, duration);

  // ER-originated SMB consultations fold a flat booking fee into the charge; show it as
  // a line item so the displayed total equals what Stripe will charge. Work in cents
  // (the fee is charged verbatim) and only round the dollar formatting at the end.
  //
  // An unknown consultation price is never blended into the total: adding a real fee to
  // an unknown rate would produce a total that looks authoritative and isn't.
  const feeCents = bookingFeeCents ?? 0;
  const showFee = pricing.type === 'dollars' && pricing.hourlyRate !== null && feeCents > 0;
  // The conditions repeated from showFee are load-bearing, not redundant: TS does not narrow
  // pricing.hourlyRate through a const boolean alias, and calculateDollarPrice takes a
  // non-null number.
  const totalDisplay =
    showFee && pricing.type === 'dollars' && pricing.hourlyRate !== null
      ? `$${formatDollars(Number(calculateDollarPrice(pricing.hourlyRate, duration)) * 100 + feeCents)}`
      : priceDisplay;

  return (
    <div className={s.container}>
      <div className={s.row}>
        <span className={s.label}>Duration</span>
        <span className={s.value}>{duration} min</span>
      </div>
      <div className={s.divider} />
      {showFee && (
        <>
          <div className={s.row}>
            <span className={s.label}>Consultation</span>
            <span className={s.value}>{priceDisplay}</span>
          </div>
          <div className={s.row}>
            <span className={s.label}>Booking fee</span>
            <span className={s.value}>${formatDollars(feeCents)}</span>
          </div>
        </>
      )}
      <div className={s.row}>
        <span className={s.label}>{showChargeNote ? 'Due when confirmed' : 'Total'}</span>
        <span className={s.totalValue}>{totalDisplay}</span>
      </div>
    </div>
  );
}

export default PriceSummary;
